import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useMasters, nameOf } from "@/lib/masters";
import { PageHeader, Card, Input, Select, Field, Btn, Stat, Chip } from "@/components/kit";
import { toast, Toaster } from "sonner";
import { FilePdf, Table as TableIcon, CalendarBlank } from "@phosphor-icons/react";
import { useAuth } from "@/lib/auth";

const WORKSHOPS = ["AMSKY", "CRON", "KODAK"];

const monthOptions = () => {
  // Last 12 months + current
  const out = [];
  const now = new Date();
  for (let i = 0; i < 13; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const y = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const from = `${y}-${mm}-01`;
    const last = new Date(y, d.getMonth() + 1, 0).getDate();
    const to = `${y}-${mm}-${String(last).padStart(2, "0")}`;
    out.push({ label: d.toLocaleString("default", { month: "long", year: "numeric" }), from, to });
  }
  return out;
};

export default function ClientLedger() {
  const { user } = useAuth();
  const m = useMasters();
  const canManageAllWorkshops = ["super_admin", "admin", "manager"].includes(user?.role);
  const assignedWorkshop = (user?.assigned_workshop || "AMSKY").toUpperCase();
  const months = monthOptions();
  const [client_id, setClient] = useState("");
  const [workshop, setWorkshop] = useState(canManageAllWorkshops ? "" : assignedWorkshop);
  const [from_date, setFrom] = useState(months[0].from);
  const [to_date, setTo] = useState(months[0].to);
  const [data, setData] = useState(null);
  const [view, setView] = useState("daywise"); // 'daywise' | 'billing'
  const [exporting, setExporting] = useState("");

  const clientName = m.clients.find(c => c.id === client_id)?.name || "";
  const periodLabel = data ? `${from_date} to ${to_date}` : "";

  const load = async () => {
    if (!client_id) return toast.error("Select a client");
    try {
      const params = { client_id };
      if (workshop) params.workshop = workshop;
      if (from_date) params.from_date = from_date;
      if (to_date) params.to_date = to_date;
      const { data } = await api.get("/reports/client_ledger", { params });
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const pickMonth = (m) => { setFrom(m.from); setTo(m.to); };

  const downloadReport = async (reportView, format) => {
    const exportKey = `${reportView}-${format}`;
    setExporting(exportKey);
    try {
      const params = { client_id, view: reportView };
      if (workshop) params.workshop = workshop;
      if (from_date) params.from_date = from_date;
      if (to_date) params.to_date = to_date;
      const response = await api.get(`/reports/client_ledger.${format}`, {
        params,
        responseType: "blob",
      });
      const blob = new Blob([response.data]);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const safeClient = clientName.replace(/[^a-z0-9]+/gi, "_").replace(/^_|_$/g, "") || "Client";
      link.href = url;
      link.download = `${safeClient}_${from_date}_to_${to_date}_${reportView}.${format}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setExporting("");
    }
  };

  return (
    <div data-testid="ledger-page">
      <Toaster position="bottom-right" richColors />
      <PageHeader
        title="Client Ledger"
        subtitle="Day-wise production detail for a client, plus a monthly billing summary grouped by plate size."
      />
      <div className="p-3 sm:p-6 space-y-4 min-w-0">
        {/* Filters */}
        <Card>
          <div className="p-3 flex flex-wrap gap-3 items-end">
            <Field label="Client">
              <Select value={client_id} onChange={e=>setClient(e.target.value)} data-testid="ledger-client-select" className="w-full min-w-0 sm:min-w-[220px]">
                <option value="">— Select —</option>
                {m.clients.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
              </Select>
            </Field>
            <Field label="Workshop">
              <Select value={workshop} onChange={e=>{setWorkshop(e.target.value); setData(null);}} data-testid="ledger-workshop-select">
                <option value="">All</option>
                {WORKSHOPS.map(item => <option key={item} value={item}>{item}</option>)}
              </Select>
            </Field>
            {!canManageAllWorkshops && workshop && workshop !== assignedWorkshop && <Chip tone="warn">View only</Chip>}
            <Field label="Quick Month">
              <Select onChange={e => {
                const idx = parseInt(e.target.value);
                if (!isNaN(idx)) pickMonth(months[idx]);
              }} defaultValue="0" data-testid="ledger-month-select">
                {months.map((mo) => <option key={mo.from} value={months.indexOf(mo)}>{mo.label}</option>)}
              </Select>
            </Field>
            <Field label="From"><Input type="date" value={from_date} onChange={e=>setFrom(e.target.value)} data-testid="ledger-from-date" /></Field>
            <Field label="To"><Input type="date" value={to_date} onChange={e=>setTo(e.target.value)} data-testid="ledger-to-date" /></Field>
            <Btn onClick={load} data-testid="ledger-run-btn">Run Report</Btn>
          </div>
        </Card>

        {data && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Stat testid="ledger-stat-jobs" label="Total Jobs" value={data.total_jobs} hint={periodLabel} />
              <Stat testid="ledger-stat-plates" label="Total Plates" value={data.total_plates} hint={clientName} />
              <Stat testid="ledger-stat-days" label="Active Days" value={data.day_groups.length} />
              <Stat testid="ledger-stat-sizes" label="Plate Sizes Used" value={data.size_totals.length} />
            </div>

            {/* View toggle */}
            <div className="flex items-center gap-2 border-b border-zinc-200 overflow-x-auto">
              <button
                data-testid="ledger-view-daywise"
                onClick={() => setView("daywise")}
                className={`shrink-0 whitespace-nowrap px-4 py-2 text-sm font-semibold border-b-2 -mb-px flex items-center gap-1.5 ${view==="daywise" ? "border-zinc-950 text-zinc-950" : "border-transparent text-zinc-500 hover:text-zinc-900"}`}
              ><CalendarBlank size={14} /> Daywise Detail</button>
              <button
                data-testid="ledger-view-billing"
                onClick={() => setView("billing")}
                className={`shrink-0 whitespace-nowrap px-4 py-2 text-sm font-semibold border-b-2 -mb-px flex items-center gap-1.5 ${view==="billing" ? "border-zinc-950 text-zinc-950" : "border-transparent text-zinc-500 hover:text-zinc-900"}`}
              ><TableIcon size={14} /> Billing Summary</button>
              <div className="ml-auto flex shrink-0 gap-2 py-1.5">
                {view === "daywise" ? (
                  <>
                    <Btn
                      variant="outline"
                      onClick={() => downloadReport("daywise", "pdf")}
                      disabled={!!exporting}
                      data-testid="ledger-export-pdf"
                    ><FilePdf size={15} /> {exporting === "daywise-pdf" ? "Preparing…" : "Export A5 PDF"}</Btn>
                    <Btn
                      onClick={() => downloadReport("daywise", "xlsx")}
                      disabled={!!exporting}
                      data-testid="ledger-export-xlsx"
                    ><TableIcon size={15} /> {exporting === "daywise-xlsx" ? "Preparing…" : "Export A5 XLSX"}</Btn>
                  </>
                ) : (
                  <>
                    <Btn
                      variant="outline"
                      onClick={() => downloadReport("billing", "pdf")}
                      disabled={!!exporting}
                      data-testid="ledger-export-billing-pdf"
                    ><FilePdf size={15} /> {exporting === "billing-pdf" ? "Preparing…" : "Export A5 PDF"}</Btn>
                    <Btn
                      onClick={() => downloadReport("billing", "xlsx")}
                      disabled={!!exporting}
                      data-testid="ledger-export-billing-xlsx"
                    ><TableIcon size={15} /> {exporting === "billing-xlsx" ? "Preparing…" : "Export A5 XLSX"}</Btn>
                  </>
                )}
              </div>
            </div>

            {/* DAYWISE VIEW */}
            {view === "daywise" && (
              <div className="space-y-4" data-testid="ledger-daywise-view">
                {data.day_groups.length === 0 && (
                  <Card><div className="p-6 text-center text-sm text-zinc-500">No jobs for this client in the selected period.</div></Card>
                )}
                {data.day_groups.map(g => (
                  <Card key={g.date} testid={`ledger-day-${g.date}`}>
                    <div className="px-4 py-2 border-b border-zinc-200 flex items-center justify-between bg-zinc-50">
                      <div className="flex items-center gap-3">
                        <CalendarBlank size={16} className="text-zinc-500" />
                        <div className="font-heading font-bold text-sm">{g.date}</div>
                        <Chip tone="neutral">{g.jobs.length} job{g.jobs.length===1?"":"s"}</Chip>
                      </div>
                      <div className="text-sm">
                        <span className="text-zinc-500">Day Total:</span>{" "}
                        <span className="font-mono font-bold text-lg">{g.total_plates}</span>
                        <span className="text-zinc-500 text-xs ml-1">plates</span>
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                    <table className="w-full min-w-[760px] data-table">
                      <thead><tr>
                        <th className="px-3 py-2 text-left">Job No.</th>
                        <th className="px-3 py-2 text-left">Job Name</th>
                        <th className="px-3 py-2 text-left">Plate Size</th>
                        <th className="px-3 py-2 text-center">S/C</th>
                        <th className="px-3 py-2 text-right">Plates</th>
                        <th className="px-3 py-2 text-left">Workshop</th>
                        <th className="px-3 py-2 text-left">Challan</th>
                      </tr></thead>
                      <tbody>
                        {g.jobs.map(j => (
                          <tr key={j.id}>
                            <td className="px-3 py-2 font-mono font-semibold">{j.job_number}</td>
                            <td className="px-3 py-2">
                              <div className="font-medium flex items-center gap-2">{j.job_name}{j.baking && <span className="rounded-full bg-amber-100 text-amber-900 border border-amber-300 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide">Baking</span>}</div>
                              {j.file_name && <div className="text-[11px] font-mono text-zinc-500 truncate max-w-xs">{j.file_name}</div>}
                            </td>
                            <td className="px-3 py-2 font-mono">{j.plate_size}</td>
                            <td className="px-3 py-2 text-center font-mono">{j.sets}S{j.colours}C</td>
                            <td className="px-3 py-2 text-right font-mono font-bold">{j.final_plate_qty}</td>
                            <td className="px-3 py-2 font-mono">{nameOf(m.machines, j.machine_id)}</td>
                            <td className="px-3 py-2 font-mono">{j.challan_number || "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    </div>
                  </Card>
                ))}
              </div>
            )}

            {/* BILLING VIEW */}
            {view === "billing" && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="ledger-billing-view">
                <Card>
                  <div className="px-4 py-3 border-b border-zinc-200">
                    <div className="font-heading font-bold text-sm uppercase tracking-wider">Billing Summary — Plate Services & Baking</div>
                    <div className="text-[11px] text-zinc-500 mt-0.5">{clientName} · {periodLabel}</div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full data-table text-xs min-w-[820px]">
                      <thead><tr>
                        <th className="px-3 py-2 text-left">Plate Size</th>
                        <th className="px-3 py-2 text-right">Jobs</th>
                        <th className="px-3 py-2 text-right">1C Sets</th>
                        <th className="px-3 py-2 text-right">2C Sets</th>
                        <th className="px-3 py-2 text-right">3C Sets</th>
                        <th className="px-3 py-2 text-right">4C Sets</th>
                        <th className="px-3 py-2 text-right">Additional</th>
                        <th className="px-3 py-2 text-right">Total Sets</th>
                        <th className="px-3 py-2 text-right">Total Plates</th>
                      </tr></thead>
                      <tbody>
                        {(data.size_totals || []).length === 0 && (data.baking_size_totals || []).length === 0 && (
                          <tr><td colSpan={9} className="text-center py-8 text-zinc-500 text-sm">No data.</td></tr>
                        )}
                        {(data.size_totals || []).length > 0 && (
                          <>
                            <tr className="bg-cyan-50 text-cyan-950">
                              <td colSpan={9} className="px-3 py-2 text-[11px] font-black uppercase tracking-widest">
                                Plate Services — {data.regular_jobs_total} jobs · {data.regular_plates_total} plates
                              </td>
                            </tr>
                            {data.size_totals.map(item => (
                              <tr key={`plate-${item.plate_size_id}`}>
                                <td className="px-3 py-2 font-mono font-bold">{item.canonical}</td>
                                <td className="px-3 py-2 text-right font-mono">{item.job_count}</td>
                                <td className="px-3 py-2 text-right font-mono">{item.one_colour_sets || 0}</td>
                                <td className="px-3 py-2 text-right font-mono">{item.two_colour_sets || 0}</td>
                                <td className="px-3 py-2 text-right font-mono">{item.three_colour_sets || 0}</td>
                                <td className="px-3 py-2 text-right font-mono">{item.four_colour_sets || 0}</td>
                                <td className="px-3 py-2 text-right font-mono font-semibold text-fuchsia-700">{item.additional_plates || 0}</td>
                                <td className="px-3 py-2 text-right font-mono">{item.total_sets || 0}</td>
                                <td className="px-3 py-2 text-right font-mono font-bold text-base">{item.actual_plates ?? item.qty}</td>
                              </tr>
                            ))}
                          </>
                        )}
                        {(data.baking_size_totals || []).length > 0 && (
                          <>
                            <tr className="bg-amber-50 text-amber-950">
                              <td colSpan={9} className="px-3 py-2 text-[11px] font-black uppercase tracking-widest">
                                Baking — {data.baking_jobs_total} jobs · {data.baking_plates_total} plates
                              </td>
                            </tr>
                            {data.baking_size_totals.map(item => (
                              <tr key={`baking-${item.plate_size_id}`}>
                                <td className="px-3 py-2 font-mono font-bold">{item.canonical}</td>
                                <td className="px-3 py-2 text-right font-mono">{item.job_count}</td>
                                <td className="px-3 py-2 text-right font-mono">{item.one_colour_sets || 0}</td>
                                <td className="px-3 py-2 text-right font-mono">{item.two_colour_sets || 0}</td>
                                <td className="px-3 py-2 text-right font-mono">{item.three_colour_sets || 0}</td>
                                <td className="px-3 py-2 text-right font-mono">{item.four_colour_sets || 0}</td>
                                <td className="px-3 py-2 text-right font-mono font-semibold text-fuchsia-700">{item.additional_plates || 0}</td>
                                <td className="px-3 py-2 text-right font-mono">{item.total_sets || 0}</td>
                                <td className="px-3 py-2 text-right font-mono font-bold text-base">{item.actual_plates ?? item.qty}</td>
                              </tr>
                            ))}
                          </>
                        )}
                      </tbody>
                      <tfoot>
                        <tr className="bg-zinc-950 text-white">
                          <td colSpan={7} className="px-3 py-3 font-heading font-black uppercase tracking-wide">
                            Grand Total — {data.total_jobs} jobs
                          </td>
                          <td className="px-3 py-3 text-right font-mono font-bold">
                            {[...(data.size_totals || []), ...(data.baking_size_totals || [])].reduce((sum, item) => sum + (item.total_sets || 0), 0)}
                          </td>
                          <td className="px-3 py-3 text-right font-mono font-black text-xl">{data.total_plates}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </Card>
                <Card>
                  <div className="px-4 py-3 border-b border-zinc-200 font-heading font-bold text-sm uppercase tracking-wider">Bill Preview</div>
                  <div className="p-6 space-y-4">
                    <div>
                      <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">Bill To</div>
                      <div className="font-heading font-bold text-lg">{clientName}</div>
                    </div>
                    <div>
                      <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">Period</div>
                      <div className="font-mono">{periodLabel}</div>
                    </div>
                    <div>
                      <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">Items</div>
                      <div className="mt-1 space-y-1 text-sm">
                          {(data.size_totals || []).length > 0 && (
                            <div className="text-[10px] uppercase tracking-widest font-bold text-cyan-800 pt-1">Plate Services</div>
                          )}
                          {(data.size_totals || []).map(item => (
                            <div key={`preview-plate-${item.plate_size_id}`} className="flex justify-between border-b border-zinc-100 py-1">
                              <span>Plate size <span className="font-mono">{item.canonical}</span> × {item.qty}</span>
                              <span className="font-mono text-zinc-500">({item.job_count} jobs)</span>
                            </div>
                          ))}
                          {(data.baking_size_totals || []).length > 0 && (
                            <div className="text-[10px] uppercase tracking-widest font-bold text-amber-800 pt-3">Baking Items</div>
                          )}
                          {(data.baking_size_totals || []).map(item => (
                            <div key={`preview-baking-${item.plate_size_id}`} className="flex justify-between border-b border-amber-100 py-1">
                              <span>Baking · Plate size <span className="font-mono">{item.canonical}</span> × {item.qty}</span>
                              <span className="font-mono text-zinc-500">({item.job_count} jobs)</span>
                            </div>
                          ))}
                      </div>
                    </div>
                    <div className="border-t-2 border-zinc-950 pt-3 flex justify-between items-baseline">
                      <span className="font-heading font-bold uppercase tracking-wider">Total Plates</span>
                      <span className="font-heading font-black text-3xl">{data.total_plates}</span>
                    </div>
                    <div className="text-[11px] text-zinc-500 italic pt-2">
                      Rates are not tracked in this system yet. Multiply plates by your per-size rate to raise the invoice.
                    </div>
                  </div>
                </Card>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
